/* eslint-disable */
import { store } from "~/state/store";
import { setCoordinates, setProvider } from "~/state/map/state";
import SceneLayerCanvas from "./layer/canvas";
import SceneLayerCanvasGeom from "./layer/canvas-geom";
import SceneLayerDistanceMeter from "./layer/distance-meter";
import SceneLayerFree from "./layer/free";
import SceneLayerGeometry from "./layer/geometry";
import SceneLayerMapyLookup from "./layer/mapy-lookup";
import SceneLayerMarker from "./layer/marker";
import SceneLayerMasakovac from "./layer/masakovac";
import SceneLayerPano from "./layer/pano";
import SceneLayerTile from "./layer/tile";
import SceneLayerTraffic from "./layer/traffic";
import SceneLayerTurist from "./layer/turist";
import SceneLayerWinter from "./layer/winter";
import { CONFIG, setMapserverFallback } from "~/config";
import ScenePixel from "~/scene/common/pixel";
import SceneCoordsInterface from "~/scene/icoords";
import SceneMarker from "~/scene/marker/marker";
import SceneMarkerGEO from "~/scene/marker/geo";
import SceneMarkerMapyPano from "~/scene/marker/mapy-pano";
import SceneMarkerPOI from "~/scene/marker/poi";
import SceneMarkerPano from "~/scene/marker/pano";
import SceneMarkerWindy from "~/scene/marker/windy";
import SceneMarkerMapyWindy from "~/scene/marker/mapy-windy";
import SceneMarkerMapyWindyDetail from "~/scene/marker/mapy-windy-detail";
import SceneMarkerMapyWindySearch from "~/scene/marker/mapy-windy-search";
import SceneMarkerFavorite from "~/scene/marker/favorite";
import SceneMarkerMapyWindyPlace from "~/scene/marker/mapy-windy-place";
import SceneMarkerFotoPOI from "~/scene/marker/fotopoi";
import SceneMarkerWindyWebcam from "~/scene/marker/windy-webcam";
import SceneMarkerBubble from "~/scene/marker/bubble";
import SceneMarkerBubblePubt from "~/scene/marker/bubble.pubt";
import ScenePano from "~/scene/common/pano";
import SceneGeometry from "~/scene/geometry/geometry";
import SceneGeometryMulti from "~/scene/geometry/multi";
import SceneGeometrySmart from "~/scene/geometry/smart";
import SceneGeometrySmartPubt from "~/scene/geometry/smart.pubt";
import SceneGeometrySmartMulti from "~/scene/geometry/smartmulti";
import SceneGeometryTraffic from "~/scene/geometry/traffic";
import SceneControlEgg from "~/scene/control/egg";
import SceneControlContextMenu from "~/scene/control/context-menu";
import MapProvider from "~/scene/provider/map/provider";
import M3DProvider from "~/scene/provider/3d/provider";
import WASMProvider from "~/scene/provider/wasm/provider";
import { ISceneLayerGeometryOpts } from "./interfaces";
import { watch, watchCoordinates } from "~/state/util";
import { ICopyright, setCopyrights } from "./utils";
import { getLayersConfig, getLayersDef } from "./config";
import type SceneLayer from "./layer/layer";
import { checkTileLoad } from "~/util/mapserver";
import { node } from "~/util/dom";
import { debounce } from "~/lib/debounce";
import { loadStatePolygon } from "~/util/state-polygon";

interface ISceneViewport {
	lbx: number;
	lby: number;
	rtx: number;
	rty: number;
}

/**
 * Typy geometrii.
 *
 * @type {Object}
 */
const GEOMETRY = {
	POLYLINE: 1,
	PATH: 2,
	POLYGON: 3,
	CIRCLE: 4,
	ELLIPSE: 5,
	TRAFFICPATH: 6
};

const MAX_ZOOM_Z = 18;
const MIN_3D_ZOOM = 4; // pri compute center zoom - nedavat mensi cislo, na co chceme videt cely svet?

function pixelToLatLon(zoom: number): { lat: number; lon: number } {
	// Earth's circumference in pixels at zoom level 0
	const earthCircumference = 256 * Math.pow(2, zoom);
	// One pixel represents 1/earthCircumference of the Earth's circumference
	const pixelSize = 1 / earthCircumference;
	// Latitude range from -85.0511° (South pole) to 85.0511° (North pole)
	const latRange = 85.0511 * 2;
	// Calculate latitude and longitude for one pixel
	const latPerPixel = latRange * pixelSize;
	const lngPerPixel = 360 * pixelSize;

	return { lat: latPerPixel, lon: lngPerPixel };
}

class Scene extends SceneCoordsInterface {
	_mapset: any;

	_visual: any;

	_config: any;

	_signals: any;

	_rootEl: HTMLElement;

	_active: any;

	_state: any;

	_providers: any;

	_itemId: number;

	_listenerId: number;

	_listeners: Array<any>;

	_ophotoDate: string;

	_layersDef: ReturnType<typeof getLayersDef>;

	constructor() {
		super();

		this._mapset = {};
		this._visual = {};
		this._config = {};
		this._signals = {};

		this._mappingId();
		this._setMapserConfig();
		this._registerSignals();

		this._rootEl = document.getElementById("scene");
		this._active = null;
		this._providers = {
			map: new MapProvider(this),
			m3d: new M3DProvider(this),
			wasm: new WASMProvider(this),
		};
		this._itemId = 0;
		this._listenerId = 0;
		this._listeners = [];
		this._state = {
			zoomRange: null,
			cursor: null,
			mapClick: null,
			markers: {}, // vytvorene veci
			layers: [],
			geometries: {},
			controls: [],
			pano: [],
			listeners: {} // posluchaci // { "map-redraw": [x,y,z] }
		};
		this._ophotoDate = "";
	}

	/* *********************** public *************************************** */

	async init() {
		const tileCheck = await checkTileLoad();

		if (!tileCheck) {
			// fallback url
			setMapserverFallback();
			// znovu nastavime configy
			this._setMapserConfig();
		}

		const { provider } = store.getState().map;
		const wt = watch(store.getState, "map", watchCoordinates);
		const debounMapRedraw = debounce(() => {
			this.updateCopyrights();
		}, 800);

		store.subscribe(wt((newMap, oldMap) => {
			if (!newMap.ignore
				&& (
					newMap.longitude !== oldMap.longitude
					|| newMap.latitude !== oldMap.latitude
					|| newMap.zoom !== oldMap.zoom
				)
			) {
				// pokud je aktivni 3d zkontrolujeme, zda nejdeme pres max zoom
				if (this._active && this.isM3dActive() && oldMap.longitude === newMap.longitude && oldMap.latitude === newMap.latitude) {
					const presenter = this._active.getPresenter();

					if (!presenter || presenter.getMaxZoom() > newMap.zoom) {
						return;
					}
				}

				this._active?.setCenterZoom(this.coordsFromWGS84(newMap.longitude, newMap.latitude), newMap.zoom, !newMap.animate).then(() => {
					store.dispatch(setCoordinates({
						animating: false,
					}));
				});
			}

			if (newMap.animate || newMap.ignore) {
				store.dispatch(setCoordinates({
					animate: false,
					ignore: false,
				}));
			}
		}));

		this.addListener("map-redraw", () => {
			if (this._active === this._providers.map) {
				debounMapRedraw();
			}
		});

		// prefetch - polygon cs
		if (provider === this._getProviderName(this._providers.map)) {
			loadStatePolygon("cs");
		}

		return this.setActive(provider);
	}

	_getProviderName(provider: string) {
		if (provider === this._providers.map) {
			return "map";
		} else if (provider === this._providers.m3d) {
			return "m3d";
		} else if (provider === this._providers.wasm) {
			return "wasm";
		} else {
			return "";
		}
	}

	/**
	 * Nastav aktivniho providera.
	 *
	 * @param {String} name Jmeno providera [map|m3d]
	 */
	async setActive(name) {
		const provider = this._providers[name];

		if (provider && provider != this._active) {
			let firstRun = this._active ? false : true;

			store.dispatch(setProvider({
				provider: name,
				providerChanging: true,
			}));

			this.makeEvent("provider-change", {
				oldProvider: this._active ? this._getProviderName(this._active) : "",
				newProvider: name,
			});

			if (this._active) {
				await this._active.deactivate();
			} else if (firstRun && name !== "map") {
				this._providers["map"].deactivate();
			}

			this._active = provider;

			await this._active.activate(this, this._state);

			// pokud se behem cekani stihl znovu zmenit i provider, pak nebudeme menit providerChanging, protoze o to se postara jine volani tehle metody
			const { provider: storeProvider } = store.getState().map;

			if (name === storeProvider) {
				store.dispatch(setProvider({ providerChanging: false }));
			}

			this.updateCopyrights();
			this.makeEvent("provider-changed", {
				firstChange: firstRun,
				name: this._active == this._providers.map ? "map" : "m3d",
			});
		}
	}

	async reload() {
		await this._active.reload(this._state);

		if (this.isM3dActive()) {
			this._state.layers.forEach(layer => {
				if (layer instanceof SceneLayerFree) {
					let cd = layer.getCreateData();
					this._active.addFreeLayer(cd.name, cd.lines, cd.style);
				}
			});
		}
	}

	get providers() {
		return this._providers;
	}

	/**
	 * Ziskani DOM elementu se scenou.
	 *
	 * @return {Element}
	 */
	getContainer() {
		return this._rootEl;
	}

	/**
	 * Ziskani rozmeru sceny, vraci pixel.
	 *
	 * @return {ScenePixel}
	 */
	getSize() {
		return new ScenePixel(this._rootEl.offsetWidth, this._rootEl.offsetHeight);
	}

	/**
	 * Ziskani objektu pri zmene dat entity.
	 *
	 * @param  {String} method Nazev metody
	 * @param  {Boolean} [runOnVisualiser = false] Spustit na visualiseru? DOM apod.
	 * @param  {Boolean} [argWithSender = false] Pridat do volani toho, kdo tuto metodu volal?
	 * @return {Object}
	 */
	featureEvent(method, runOnVisualiser?, argWithSender?) {
		if (!method) return null;

		let obj: any = {
			method: method
		};

		if (runOnVisualiser) {
			obj.vis = true;
		}

		if (argWithSender) {
			obj.sender = true;
		}

		return obj;
	}

	/**
	 * Zmena od entity.
	 *
	 * @param  {Object} sender Kdo udalost vyvolal?
	 * @param  {Object} featureItem Object s definici udalost - vytvoreny pres featureEvent
	 * @param  {Array} [args] Volitelne argumenty
	 * @return {Object} Zalezi dle typy volane funkce
	 */
	featureChange(sender, featureItem, args) {
		return (this._active ? this._active.featureChange(sender, featureItem, Array.prototype.slice.call(args || [], 0)) : null);
	}

	/**
	 * Ziskani objektu s daty pro definici signalu - vetsinou bude potrebovat provider napr. pro definici dat, ktere ma signal vracet.
	 *
	 * @param  {String} name Nazev signalu
	 * @return {Object}
	 */
	getSignal(name) {
		return (name in this._signals ? this._signals[name] : null);
	}

	/**
	 * Ziskani URL pro ikonu.
	 *
	 * @param  {Number} id ID ikony
	 * @return {String}
	 */
	getIconURL (id) {
		// v pripade potreby pro server testovat namespace SMap
		return (SMap.CONFIG.poiimg + "/icon/" + id);
	}

	/**
	 * Je aktivni provider mapa?
	 *
	 * @return {Boolean}
	 */
	isMapActive () {
		return this._active == this._providers.map;
	}

	/**
	 * Je aktivni provider 3d?
	 *
	 * @return {Boolean}
	 */
	isM3dActive () {
		return this._active == this._providers.m3d;
	}

	/**
	 * Je aktivni provider wasm?
	 *
	 * @return {Boolean}
	 */
	isWasmActive () {
		return this._active == this._providers.wasm;
	}

	/**
	 * Vytvoreni obrazku se screenshotem 3d.
	 *
	 * @param {Boolean} [showLogo = true] zobrazit logo
	 * @return {Object} src, width, height
	 */
	get3dScreenshot (showLogo = true) {
		return this._providers.m3d.getScreenshot(showLogo);
	}

	/* *********************** enumy *************************************** */

	/**
	 * Ziskani enumu pro definici mapsetu.
	 *
	 * @return {Object}
	 */
	get MAPSET() {
		return this._mapset;
	}

	/**
	 * Ziskani enumu pro definici geometrii.
	 *
	 * @return {Object}
	 */
	get GEOMETRY() {
		return GEOMETRY;
	}

	/**
	 * Ziskani enumu pro vizualni prvky - markery, kapka.
	 *
	 * @return {Object}
	 */
	get VISUAL() {
		return this._visual;
	}

	/**
	 * Ziskani enumu pro konfiguraci sceny.
	 *
	 * @return {Object}
	 */
	get CONFIG() {
		return this._config;
	}

	// zatim pouze pro wasm
	clearAllWasmContent() {
		if (this.isWasmActive()) {
			this._providers.wasm.clearAllWasmContent();
		}
	}

	/* *********************** signaly *************************************** */

	/**
	 * Pridat posluchace udalosti - seznam this._signals.
	 *
	 * @param {String}   name jmeno signalu
	 * @param {Function|String} cb Callback funkce
	 * @param {Object} [Scope] pro funkci
	 * @param {Object} [sender] Pro jaky objekt poslouchat?
	 * @return {Number} ID cislo signalu
	 */
	addListener(name, cb, scope?, sender?) {
		let signalData = name in this._signals ? this._signals[name] : null;

		if (!signalData) {
			// eslint-disable-next-line no-console
			console.error("Unknown signal " + name);
			return null;
		}

		let id = this._listenerId;
		this._listenerId++;

		let item = {
			id: id,
			name: name,
			cb: cb,
			scope: scope,
			sender: sender,
			noProvider: signalData.noProvider
		};

		this._listeners.push(item);
		this._addSignal(item);

		return id;
	}

	/**
	 * Odebrat posluchace udalosti - seznam this._signals.
	 *
	 * @param {String}   name jmeno signalu
	 * @param {Function|String} cb Callback funkce
	 * @param {Object} [Scope] pro funkci
	 * @param {Object} [sender] Pro jaky objekt poslouchat?
	 * @chainable
	 */
	removeListener(name, cb, scope, sender?) {
		// state
		let len = this._listeners.length - 1;

		for (let i = len; i >= 0; i--) {
			let item = this._listeners[i];

			if (item.data.name == name && (!cb || item.data.cb == cb) && (!scope || item.data.scope == scope) && (!sender || item.data.sender == sender)) {
				this._listeners.splice(i, 1);
				this._removeSignal(item);
			}
		}

		return this;
	}

	/**
	 * Odebrat posluchace udalosti - bud jeden nebo cele pole s jejich ID
	 *
	 * @param {Number|Array[Number]} id ID signalu
	 * @chainable
	 */
	removeListeners(id) {
		let len = this._listeners.length - 1;
		let ids = Array.isArray(id) ? id : [id];

		for (let i = len; i >= 0; i--) {
			let item = this._listeners[i];
			let ind = ids.indexOf(item.id);

			if (ind != -1) {
				ids.splice(ind, 1);

				this._listeners.splice(i, 1);
				this._removeSignal(item);
			}

			if (!ids.length) break;
		}

		return this;
	}

	/**
	 * Vytvor udalost nad scenou - muze volat sama scena nebo i provider.
	 *
	 * @param  {String} name Nazev signalu, musi byt definovany v this._signals
	 * @param  {Object} arg Data signalu
	 * @param  {Object} sender Kdo vyvolal udalost - pokud nekdo specificky posloucha primo na objektu, tak se vyvola udalost pouze pri shode
	 * @chainable
	 */
	makeEvent(name, arg?, sender?) {
		if (!this._signals[name]) {
			// eslint-disable-next-line no-console
			console.error("Cannot run signal name " + name + "!");
			return;
		}

		this._listeners.forEach(listener => {
			if (listener.name != name || (listener.sender && sender && sender != listener.sender)) return;

			let event = {
				data: arg,
				type: name,
				target: sender,
			};

			let func = typeof listener.cb === "function" ? listener.cb : listener.scope[listener.cb];
			func.apply(listener.scope || func, [event]);
		});

		// event do storu pri uprave vrstev
		if (name === "layer-enable" || name === "layer-disable" || name === "zoom-stop") {
			this.updateCopyrights();
		}

		return this;
	}

	/* *********************** coords a provider *************************************** */

	/**
	 * Vytvoreni souradnice z eventu. Vezme se pozice kliku, prepocita na souradnice.
	 *
	 * @param  {Event} e
	 * @return {SceneCoords}
	 */
	coordsFromEvent(event) {
		if (this._active) {
			if (this.isWasmActive()) {
				const merc = this._providers.wasm.screenPosToMapPos(event.offsetX, event.offsetY);

				return this.coordsFromMercator(merc.x, merc.y);
			} else {
				const pixel = this.pixelFromEvent(event);

				return this._active.pixelToCoords(pixel);
			}
		} else {
			// eslint-disable-next-line no-console
			console.error("No active provider!");
			return null;
		}
	}

	/**
	 * Vytvoreni pixelu z eventu. Vezme se pozice kliku, prepocita na pixel.
	 *
	 * @param  {Event} e
	 * @return {ScenePixel}
	 */
	pixelFromEvent(e) {
		if (this.isMapActive()) {
			let c = this.getContainer();

			let size = this.getSize();
			let sizePixel = new ScenePixel(size.x, size.y).clone().scale(0.5);

			let rect = c.getBoundingClientRect();
			let left = rect.left + (sizePixel.x + (c.clientLeft || 0));
			let top = rect.top + (sizePixel.y + (c.clientTop || 0));

			return new ScenePixel((e.clientX - left) || 0, (e.clientY - top) || 0);
		}
		else {
			return (new ScenePixel(e.clientX, e.clientY));
		}
	}

	/**
	 * Spočítá střed a zoom pro množinu bodů
	 *
	 * @param {SceneCoords[]} arr Pole souřadnic
	 * @param {bool} [usePadding=false] Má-li se průhled zúžit o paddingy způsobené ovládacími prvky
	 * @param {integer} [overlap] tolerance prekryvu viewportu v procentech
	 * @returns {array} Střed a zoom
	 */
	computeCenterZoom(arr, usePadding?, overlap?) {
		/**
		 * Nyni je implementovano zjednodusene reseni, zatim vyhovuje.
		 * Dve moznosti reseni:
		 * - Implementovat pripadne tento algoritmus:
		 * 	 	[https://gitlab.kancelar.seznam.cz]/mapycz/mapy-mapserver/blob/master/compositor/src/compose_impl.cc [computeCenterZoom]
		 * - Jde o to ze se nejdriv BBOX geometrie prevede do mercatoru (projekce ve ktery chceme mapu), zjisti se velikost tohle BBOXu v
		 *   pixelech na zoomu 0 a to se pak prevede na aktualni sirku a vysku obrazku/viewportu. A pak uz se vezme jenom ten mensi pomer
		 *   a prevede na zoom klasickym zpusobem.
		 */

		if (this._active) {
			let minX = Infinity;
			let minY = Infinity;
			let maxX = -Infinity;
			let maxY = -Infinity;

			for (let i = 0; i < arr.length; i++) {
				let item = arr[i].toWGS84();
				minX = Math.min(minX, item[0]);
				minY = Math.min(minY, item[1]);
				maxX = Math.max(maxX, item[0]);
				maxY = Math.max(maxY, item[1]);
			}

			let x = (minX + maxX) / 2;
			let y = (minY + maxY) / 2;

			/* pocet stupnu, ktere chceme zobrazit */
			let diffX = maxX - minX;
			let diffY = maxY - minY;
			let zoom;

			if (this.isM3dActive()) {
				diffX *= 1.5;
				diffY *= 1.5;
				zoom = Math.max(this._active.countZoom(diffX, diffY), MIN_3D_ZOOM);
			} else {
				zoom = this._active.countZoom(diffX, diffY, usePadding, overlap);
			}

			let zoomRange = this._state.zoomRange;
			let maxZoom = zoomRange ? zoomRange.max : MAX_ZOOM_Z;

			if (zoom == Infinity || zoom > maxZoom || isNaN(zoom)) {
				zoom = maxZoom;
			}

			if (usePadding && zoom < 1/0 && this._active != this._providers.m3d) { /* posunout stred kvuli paddingu, pokud mame smysluplny zoom */ // nechceme pocitat pro 3d
				const padding = this._active.getPadding();
				const dx = -(padding.left - padding.right) / 2;
				const dy = -(padding.top - padding.bottom) / 2;
				const { lat, lon } = pixelToLatLon(zoom);

				return [this.coordsFromWGS84(x + (dx * lon), y + (dy * lat)), zoom];
			}

			return [this.coordsFromWGS84(x, y), zoom];
		} else {
			// eslint-disable-next-line no-console
			console.error("No active provider!");
			return null;
		}
	}

	/**
	 * Nové souřadnice, které se od zadaných na zadaném zoomu liší o padding mapy
	 *
	 * @param {Coords} coords Souřadnice, které posouváme
	 * @param {int} [zoom] Zoom, ve kterém je definovaný pixelový posun
	 * @returns {SceneCoords}
	 */
	adjustCoordsByPadding(coords, zoom) {
		if (this._active == this._providers.map) {
			// mapa
			let padding = this._active.getPadding();
			let dx = padding.right - padding.left;
			let dy = padding.bottom - padding.top;
			let diff = new ScenePixel(dx / 2, dy / 2);
			let providerPixel = this._active.coordsToPixel(coords, zoom).plus(diff);

			return this._active.pixelToCoords(providerPixel, zoom);
		}
		else {
			// 3d a ostatni pripady
			return coords;
		}
	}

	/* *********************** provider proxy *************************************** */

	/**
	 * Nastaveni callback funkce pro kliknuti do mapy. Pouziti na starem userwebu, vyhnout se ve windy!
	 *
	 * @param {Function} cb Callback funkce
	 */
	setMapClick(cb) {
		// state
		this._state.mapClick = cb;

		// propagation
		this.featureChange(this, this.featureEvent("setMapClick"), arguments);
	}

	/**
	 * Nastav kurzor provideru.
	 *
	 * @param {String} cursor Styl pro kurzor
	 */
	setCursor(cursor) {
		// state
		this._state.cursor = cursor;

		// propagation
		this.featureChange(this, this.featureEvent("setCursor"), arguments);
	}

	/**
	 * Ziskani objektu s paddingem - jak mapa, tak 3d
	 *
	 * @return {Object}
	 */
	getPadding() {
		if (this._active) {
			return this._active.getPadding();
		}
		else {
			// eslint-disable-next-line no-console
			console.error("No active provider!");
			return null;
		}
	}

	/**
	 * Ziskani DOMu pro obal vrstvy. Neda se bohuzel udelat jinak, pouziti pouze na starem userwebu!
	 *
	 * @return {Element}
	 */
	getLayerMarkerContainer() {
		if (this._active) {
			return this._active.getLayerMarkerContainer();
		}
		else {
			// eslint-disable-next-line no-console
			console.error("No active provider!");
			return null;
		}
	}

	/**
	 * Ziskani aktualniho viewportu ve WGS84
	 *
	 * @return {Object} klice lb (left bottom), rt (right top) + osy x a y
	 */
	getViewport(): ISceneViewport {
		if (this._active) {
			return this._active.getViewport();
		}
		else {
			// eslint-disable-next-line no-console
			console.error("No active provider!");
			return null;
		}
	}

	/**
	 * Nastaveni paddingu.
	 *
	 * @param {String} which Typ - left, top...
	 * @param {Number} value Hodnota
	 */
	setPadding(which, value) {
		if (this._active) {
			return this._active.setPadding(which, value);
		}
		else {
			// eslint-disable-next-line no-console
			console.error("No active provider!");
			return null;
		}
	}

	/**
	 * Nastaveni rozsahu pro zoom.
	 */
	setZoomRange(min: number, max: number) {
		this._state.zoomRange = { min, max };

		if (this._active) {
			this._active.setZoomRange(min, max);
		}
	}

	/**
	 * Cteni rozsahu pro zoom.
	 */
	getZoomRange() {
		// FIXME: ve 3D netusime, takze si musime fejkovat, protoze stavajici stav je, ze se tlacitka neumi disablovat a lze se doklikat i do zoomu pres 20
		return this.isM3dActive() ? [2, 20] : [this._state.zoomRange.min, this._state.zoomRange.max];
	}

	/**
	 * Spocitani zoomu pro zobrazeni delky x a y na zemekouli. Chceme zapocist i padding?
	 *
	 * @param {Number} lenX Delka na rovnobezce
	 * @param {Number} lenY Delka na poledniku
	 * @param {Boolean} [usePadding = false]
	 * @return {Number} Vysledny zoom
	 */
	countZoom(lenX, lenY, usePadding) {
		if (this._active) {
			return this._active.countZoom(lenX, lenY, usePadding);
		}
		else {
			// eslint-disable-next-line no-console
			console.error("No active provider!");
			return null;
		}
	}

	/**
	 * Uzamknuti providera.
	 */
	lock() {
		if (this._active) {
			this._active.lock();
		}
	}

	/**
	 * Odemknuti providera.
	 */
	unlock() {
		if (this._active) {
			this._active.unlock();
		}
	}

	/**
	 * Znovu prepocet rozmery providera - roztahnuti do aktulniho kontentu.
	 */
	syncPort() {
		if (this._active) {
			this._active.syncPort();
		}
	}

	/**
	 * Prekresleni providera.
	 */
	redraw() {
		if (this._active) {
			this._active.redraw();
		}
	}

	/**
	 * Prevod pixelu na souradnice pomoci projekct providera.
	 *
	 * @param  {ScenePixel} pixel Vstupni pixel
	 * @param  {Number} [zoom] Vstupni zoom
	 * @return {SceneCoords}
	 */
	pixelToCoords(pixel, zoom?) {
		if (this._active) {
			return this._active.pixelToCoords(pixel, zoom);
		}
		else {
			// eslint-disable-next-line no-console
			console.error("No active provider!");
			return null;
		}
	}

	/**
	 * Prevod souradnic na pixel pomoci projekct providera.
	 *
	 * @param  {SceneCoords} pixel Vstupni souradnice
	 * @param  {Number} [zoom] Vstupni zoom
	 * @return {ScenePixel}
	 */
	coordsToPixel(coords, zoom?) {
		if (this._active) {
			return this._active.coordsToPixel(coords, zoom);
		}
		else {
			// eslint-disable-next-line no-console
			console.error("No active provider!");
			return null;
		}
	}

	/**
	 * Prevod souradnic na pixel pomoci projekct providera.
	 *
	 * @param  {SceneCoords} pixel Vstupni souradnice
	 * @param  {Number} [zoom] Vstupni zoom
	 * @return {ScenePixel}
	 */
	pixelToTile(pixel, tileSize) {
		if (this._active) {
			return this._active.pixelToTile(pixel, tileSize);
		}
		else {
			// eslint-disable-next-line no-console
			console.error("No active provider!");
			return null;
		}
	}

	/**
	 * Prevod dlazdice na pixel pomoci projekct providera.
	 *
	 * @param  {SceneTile} tile Vstupni Dlazdice
	 * @return {ScenePixel}
	 */
	tileToPixel(tile) {
		if (this._active) {
			return this._active.tileToPixel(tile);
		}
		else {
			// eslint-disable-next-line no-console
			console.error("No active provider!");
			return null;
		}
	}

	/**
	 * Vyplneni kontext menu - nepouzivat na widy!
	 */
	fillContextMenu() {
		if (this._active) {
			return this._active.fillContextMenu();
		}
		else {
			// eslint-disable-next-line no-console
			console.error("No active provider!");
			return null;
		}
	}

	/**
	 * Zavreni kontext menu - nepouzivat na widy!
	 */
	closeContextMenu() {
		if (this._active) {
			return this._active.closeContextMenu();
		}
		else {
			// eslint-disable-next-line no-console
			console.error("No active provider!");
			return null;
		}
	}

	/**
	 * Ziskani controlu pro ovladani pomoci klavesnice - nepouzivat na widy!
	 */
	getKeyboardControl() {
		if (this._active) {
			return this._active.getKeyboardControl();
		}
		else {
			// eslint-disable-next-line no-console
			console.error("No active provider!");
			return null;
		}
	}

	/**
	 * Ziskani controlu pro ovladani pomoci mysi
	 */
	getMouseControl() {
		if (this._active) {
			return this._active.getMouseControl();
		}
		else {
			// eslint-disable-next-line no-console
			console.error("No active provider!");
			return null;
		}
	}

	/**
	 * Pripnut vrstvy do sceny.
	 *
	 * @param {SceneLayer} layer Vrstva
	 * @param {bool} [before] Připnout-li vrstvu dospod ostatních (default = false = navrch)
	 */
	addLayer(layer, before = false) {
		if (layer.appended) return layer;

		// state
		layer.appended = true;
		layer.sceneOwner = this;
		layer.setBefore(before);

		// propagation
		this.featureChange(this, this.featureEvent("addLayer", true), arguments);

		return layer;
	}

	/**
	 * Odepnuti vrstvy ze sceny.
	 *
	 * @param {SceneLayer} layer Vrstva
	 */
	removeLayer(layer) {
		if (!layer.appended) return layer;

		// state
		layer.appended = false;
		layer.sceneOwner = null;

		// propagation
		this.featureChange(this, this.featureEvent("removeLayer", true), arguments);

		return layer;
	}

	/**
	 * Pridani ovladaciho prvku do sceny.
	 *
	 * @param {SceneControl} control
	 */
	addControl(control) {
		if (control.appended) return control;

		// state
		control.appended = true;
		control.sceneOwner = this;

		// propagation
		this.featureChange(this, this.featureEvent("addControl", true), arguments);

		return control;
	}

	/**
	 * Odebrani ovladaciho ze sceny.
	 *
	 * @param {SceneControl} control
	 */
	removeControl(control) {
		if (!control.appended) return control;

		// state
		control.appended = false;
		control.sceneOwner = null;

		// propagation
		this.featureChange(this, this.featureEvent("removeControl", true), arguments);

		return control;
	}

	addPreviewMap(type) {
		this.featureChange(this, this.featureEvent("addPreviewMap", true), arguments);
	}

	addFrameWarning() {
		this.featureChange(this, this.featureEvent("addFrameWarning", true), arguments);
	}

	/**
	 * Odstraneni entity - destroy u entit nebo si to muzu zavolat sam.
	 * Upravuje aktualni stav sceny.
	 *
	 * @param {SceneFeature} sceneItem Prvek ve scene.
	 */
	deleteEntity(sceneItem) {
		let fi = sceneItem.featureInfo;
		let id = sceneItem.itemId;

		if (fi.type == "marker" && id in this._state.markers) {
			// state
			delete this._state.markers[id];
		}
		else if (fi.type == "layer") {
			let ind = this._state.layers.indexOf(sceneItem);

			if (ind != -1) {
				// state
				this._state.layers.splice(ind, 1);
			}
		}
		else if (fi.type == "geometry" && id in this._state.geometries) {
			// state
			delete this._state.geometries[id];
		}
		else if (fi.type == "control") {
			let ind = this._state.controls.indexOf(sceneItem);

			if (ind != -1) {
				// state
				this._state.controls.splice(ind, 1);
			}
		}
		else if (fi.type == "pano") {
			let ind = this._state.pano.indexOf(sceneItem);

			if (ind != -1) {
				// state
				this._state.pano.splice(ind, 1);
			}
		}
	}

	removeCard() {
		this.featureChange(this, this.featureEvent("removeCard", true), arguments);
	}

	/* *********************** vytvareni entit *************************************** */

	/**
	 * Vytvoreni obycejneho markeru.
	 *
	 * @param  {SceneCoords} coords Souradnice
	 * @param  {Number|String} [id] Volitelne ID
	 * @param  {Object} [opts] Volitelny objekt s konfiguraci
	 * @return {SceneMarker}
	 */
	createMarker(coords, id, opts) {
		return this._postCreateMarker(new SceneMarker(this, this._itemId++, coords, id, opts));
	}

	/**
	 * Vytvoreni mapy markeru - GEO.
	 *
	 * @param  {SceneCoords} coords Souradnice
	 * @return {SceneMarkerMapy}
	 */
	createMarkerGEO(coords) {
		return this._postCreateMarker(new SceneMarkerGEO(this, this._itemId++, coords));
	}

	/**
	 * Vytvoreni markeru pro panoramu - upravena verze.
	 *
	 * @param  {SceneCoords} coords Souradnice
	 * @return {SceneMarkerMapyPano}
	 */
	createMarkerMapyPano(coords) {
		return this._postCreateMarker(new SceneMarkerMapyPano(this, this._itemId++, coords));
	}

	/**
	 * Vytvoreni POI markeru.
	 *
	 * @param  {SceneCoords} coords Souradnice
	 * @param  {Number|String} [id] Volitelne ID
	 * @param  {Object} [opts] Volitelny objekt s konfiguraci
	 * @return {SceneMarkerPOI}
	 */
	createMarkerPOI(coords, id, opts) {
		return this._postCreateMarker(new SceneMarkerPOI(this, this._itemId++, coords, id, opts));
	}

	/**
	 * Vytvoreni markeru pro panoramu napr. - API verze
	 *
	 * @param  {SceneCoords} coords Souradnice
	 * @param  {Number|String} [id] Volitelne ID
	 * @param  {Object} [opts] Volitelny objekt s konfiguraci
	 * @return {SceneMarkerPano}
	 */
	createMarkerPano(coords, id, opts) {
		return this._postCreateMarker(new SceneMarkerPano(this, this._itemId++, coords, id, opts));
	}

	/**
	 * Vytvoreni markeru pro panoramu napr. - API verze
	 *
	 * @param  {SceneCoords} coords Souradnice
	 * @param  {Number|String} [id] Volitelne ID
	 * @param  {Object} [opts] Volitelny objekt s konfiguraci
	 * @return {SceneMarkerWindy}
	 */
	createMarkerWindy(coords, id?, opts?) {
		return this._postCreateMarker(new SceneMarkerWindy(this, this._itemId++, coords, id, opts));
	}

	/**
	 * Vytvoreni WINDY POI markeru.
	 *
	 * @param  {SceneCoords} coords Souradnice
	 * @param  {Number|String} [id] Volitelne ID
	 * @param  {Object} [opts] Volitelny objekt s konfiguraci
	 * @return {SceneMarkerMapyWindy}
	 */
	createMarkerMapyWindy(coords, id, opts) {
		return this._postCreateMarker(new SceneMarkerMapyWindy(this, this._itemId++, coords, id, opts));
	}

	/**
	 * Vytvoreni WINDY POI markeru z POI dat.
	 *
	 * @param  {Object} data Vstupni data
	 * @param  {Number|String} [id] Volitelne ID
	 * @param  {Object} [opts] Volitelny objekt s konfiguraci
	 * @return {SceneMarkerMapyWindy}
	 */
	createMarkerMapyWindyFromPOI(data, id, opts) {
		let sceneMarker = new SceneMarkerMapyWindy(this, this._itemId++);
		sceneMarker.fromPOI(data, id, opts);

		// state
		this._state.markers[sceneMarker.itemId] = sceneMarker;

		// propagation
		this.featureChange(this, this.featureEvent("createMarker", true), [sceneMarker]);

		return sceneMarker;
	}

	/**
	 * Vytvoreni WINDY POI markeru z POI dat.
	 *
	 * @param  {Object} data Vstupni data
	 * @return {SceneMarkerMapyWindy}
	 */
	createMarkerMapyWindyFromData(data) {
		let sceneMarker = new SceneMarkerMapyWindy(this, this._itemId++);
		sceneMarker.fromData(data);

		// state
		this._state.markers[sceneMarker.itemId] = sceneMarker;

		// propagation
		this.featureChange(this, this.featureEvent("createMarker", true), [sceneMarker]);

		return sceneMarker;
	}

	/**
	 * Vytvoreni WINDY POI markeru pro detail
	 *
	 * @param  {Object} poi Vstupni POI data
	 * @param  {Number|String} [id] Volitelne ID
	 * @param  {Object} [opts] Volitelny objekt s konfiguraci
	 * @return {SceneMarkerMapyWindyDetail}
	 */
	createMarkerMapyWindyDetailFromPOI(poi, id, opts) {
		let sceneMarker = new SceneMarkerMapyWindyDetail(this, this._itemId++);
		sceneMarker.fromPOI(poi, id, opts);

		// state
		this._state.markers[sceneMarker.itemId] = sceneMarker;

		// propagation
		this.featureChange(this, this.featureEvent("createMarker", true), [sceneMarker]);

		return sceneMarker;
	}

	/**
	 * Vytvoreni WINDY POI markeru pro hledani z POI dat
	 *
	 * @param  {Object} data Vstupni POI data
	 * @param  {Number|String} [id] Volitelne ID
	 * @param  {Object} [opts] Volitelny objekt s konfiguraci
	 * @return {SceneMarkerMapyWindyDetail}
	 */
	createMarkerMapyWindySearchFromPOI(data, id, opts) {
		let sceneMarker = new SceneMarkerMapyWindySearch(this, this._itemId++);
		sceneMarker.fromPOI(data, id, opts);

		// state
		this._state.markers[sceneMarker.itemId] = sceneMarker;

		// propagation
		this.featureChange(this, this.featureEvent("createMarker", true), [sceneMarker]);

		return sceneMarker;
	}

	/**
	 * Vytvoreni WINDY POI markeru pro mojemapy
	 *
	 * @param  {SceneCoords} coords Souradnice
	 * @param  {Object} [item] item
	 * @param  {Object} [opts] Volitelny objekt s konfiguraci
	 * @return {SceneMarkerMapyWindy}
	 */
	createMarkerMapyWindyPlace(coords, item, opts = {}, id?) {
		return this._postCreateMarker(new SceneMarkerMapyWindyPlace(this, (this._itemId++).toString(), coords, item, opts, id));
	}

	/**
	 * Vytvoreni favorite POI markeru pro mojemapy
	 *
	 * @param  {SceneCoords} coords Souradnice
	 * @param  {Object} [item] item
	 * @param  {Object} [opts] Volitelny objekt s konfiguraci
	 * @return {SceneMarkerFavorite}
	 */
	createMarkerFavorite(coords, item, opts = {}, id?) {
		return this._postCreateMarker(new SceneMarkerFavorite(this, (this._itemId++).toString(), coords, item, opts, id));
	}

	/**
	 * Vytvoreni POI markeru pro foto vrstvu z POI dat
	 *
	 * @param  {Object} poi Vstupni POI data
	 * @return {SceneMarkerFotoPOI}
	 */
	createMarkerFotoFromData(poi) {
		let sceneMarker = new SceneMarkerFotoPOI(this, this._itemId++);
		sceneMarker.fromData(poi);

		// state
		this._state.markers[sceneMarker.itemId] = sceneMarker;

		// propagation
		this.featureChange(this, this.featureEvent("createMarker", true), [sceneMarker]);

		return sceneMarker;
	}

	/**
	 * Vytvoreni POI markeru pro foto vrstvu z POI dat
	 *
	 * @param  {Object} poi Vstupni POI data
	 * @return {SceneMarkerFotoPOI}
	 */
	createMarkerWindyWebcamFromData(poi) {
		let sceneMarker = new SceneMarkerWindyWebcam(this, this._itemId++);
		sceneMarker.fromData(poi);

		// state
		this._state.markers[sceneMarker.itemId] = sceneMarker;

		// propagation
		this.featureChange(this, this.featureEvent("createMarker", true), [sceneMarker]);

		return sceneMarker;
	}

	/**
	 * Vytvoreni markeru bubliny.
	 *
	 * @param  {SceneCoords} coords Souradnice
	 * @param  {Number|String} [id] Volitelne ID
	 * @param  {Object} [opts] Volitelny objekt s konfiguraci
	 * @return {SceneMarker}
	 */
	createMarkerBubble(coords, id, opts) {
		return this._postCreateMarker(new SceneMarkerBubble(this, this._itemId++, coords, id, opts));
	}

	createMarkerBubblePubt(coords, id, opts) {
		return this._postCreateMarker(new SceneMarkerBubblePubt(this, this._itemId++, coords, id, opts));
	}

	/**
	 * Vytvoreni vrstvy z map set ID - je potreba vrstvu rucne pripnout (narozdil od smap)
	 *
	 * @param  {Number} mapsetId Enum s mapsety
	 * @return {SceneLayer} Vytvorena vrstva
	 */
	createLayerFromMapset(mapsetId) {
		let conf = this._config;
		let layer = null;
		let item;
		let itemOptions;

		switch (mapsetId) {
			case this._mapset.OBLIQUE:
			case this._mapset.OBLIQUE_HYBRID:
				item = conf[mapsetId];
				this._updateTileUrl(item);
				itemOptions = Object.assign({
					retinaUrl: item.retinaUrl || ""
				}, item.options);

				// doufam, ze bude stacit tile a nemusim delat SMap.Layer.Tile.Oblique
				layer = new SceneLayerTile(this, this._itemId++, mapsetId, item.url, itemOptions);
			break;

			default:
				if (!(mapsetId in conf)) { throw new Error("Invalid default layer id " + mapsetId); }

				item = conf[mapsetId];
				this._updateTileUrl(item);
				itemOptions = {
					retinaUrl: item.retinaUrl || "",
					...item.options,
					...CONFIG.isScreenshoter ? { fadeTime : 0 } : {},
				};

				let ctor = SceneLayerTile;

				if ([this._mapset.TURIST, this._mapset.TURIST].indexOf(mapsetId) != -1) { ctor = SceneLayerTurist; }

				layer = new ctor(this, this._itemId++, mapsetId, item.url, itemOptions);
			break;
		}

		if (layer) {
			// nastavime
			layer.setMapset();
			layer.setCopyright(item.copyright);

			// state
			this._state.layers.push(layer);

			// propagation
			this.featureChange(this, this.featureEvent("createLayerFromMapset", true), [layer]);
		}

		return layer;
	}

	createPanoLayer(id, opts) {
		return this._postCreateLayer(new SceneLayerPano(this, this._itemId++, id, "", opts));
	}

	/**
	 * Vytvoreni vrstvy pro markery.
	 *
	 * @param  {String|Number} [id] Volitelne ID
	 * @return {SceneLayerMarker}
	 */
	createLayerMarker(id?: string) {
		return this._postCreateLayer(new SceneLayerMarker(this, this._itemId++, id));
	}

	/**
	 * Vytvoreni uber vrstvy masakovace.
	 *
	 * @return {SceneLayerMasakovac}
	 */
	createLayerMasakovac() {
		return this._postCreateLayer(new SceneLayerMasakovac(this, this._itemId++));
	}

	/**
	 * Vytvoreni vrstvy pro geometrie.
	 *
	 * @param  {String|Number} [id] Volitelne ID
	 * @return {SceneLayerGeometry}
	 */
	createLayerGeometry(id?, opts?: ISceneLayerGeometryOpts) {
		return this._postCreateLayer(new SceneLayerGeometry(this, this._itemId++, id, opts));
	}

	/**
	 * Vytvoreni vrstvy pro zobrazeni dlazdic.
	 *
	 * @param  {String|Number} id Volitelne ID
	 * @param  {String} url URL pro tahani dlazdic
	 * @param  {Object} [options] Volitelny objekt pro nastaveni vrstvy
	 * @return {SceneLayerGeometry}
	 */
	createLayerTile(id?, url?, options?) {
		return this._postCreateLayer(new SceneLayerTile(this, this._itemId++, id, url, options));
	}

	/**
	 * Vytvoreni vrstvy pro canvas.
	 *
	 * @param  {String|Number} [id] Volitelne ID
	 * @param  {String|Number} [layerId] Volitelne layer ID
	 * @return {SceneLayerCanvas}
	 */
	createLayerCanvas(id, layerId) {
		return this._postCreateLayer(new SceneLayerCanvas(this, this._itemId++, id, layerId));
	}

	/**
	 * Vytvoreni turisticke vrstvy.
	 *
	 * @param  {String|Number} [id] Volitelne ID
	 * @return {SceneLayerMarker}
	 */
	createLayerTurist(id) {
		return this._postCreateLayer(new SceneLayerTurist(this, this._itemId++, id));
	}

	/**
	 * Vytvoreni multi vrstvy pro zobrazeni POIu a geometrii.
	 *
	 * @param  {String|Number} [id] Volitelne ID
	 * @return {SceneLayerMapyLookup}
	 */
	createLayerMapyLookup(id) {
		return this._postCreateLayer(new SceneLayerMapyLookup(this, this._itemId++, id));
	}

	/**
	 * Vytvoreni vrstvy pro zobrazeni trasy ve 3d.
	 *
	 * @param {String} name ID-nazev vrstvy
	 * @param {Array[Array[x, y]]} lines Body trasy - pole poli
	 * @param {Object} [style] Styl pro vykresleni vrstvy
	 * @return {SceneLayerFree}
	 */
	createLayerFree(name, lines, style) {
		return this._postCreateLayer(new SceneLayerFree(this, this._itemId++, name, lines, style));
	}

	/**
	 * Vytvoreni vrstvy pro 3d - zobrazeni rucniho mereni.
	 *
	 * @param {Array} points Body rucniho mereni WGS84 [x,y]
	 * @param {Array} linePoints Body rucniho mereni pro vykresleni car WGS84 [x,y,z]
	 * @param {Object} style Vizaulni nastaveni vrstvy
	 * @return {SceneLayerFree}
	 */
	createLayerDistanceMeter(points, linePoints, style) {
		return this._postCreateLayer(new SceneLayerDistanceMeter(this, this._itemId++, points, linePoints, style));
	}

	/**
	 * Vytvoreni vrstvy pro zobrazeni trafficu.
	 *
	 * @param {String} name ID-nazev vrstvy
	 * @return {SceneLayerTraffic}
	 */
	createLayerTraffic(id, options) {
		return this._postCreateLayer(new SceneLayerTraffic(this, this._itemId++, id, options));
	}

	/**
	 * Vytvoreni vrstvy pro zobrazeni svg geometrii pomoci canvasu.
	 *
	 * @param {String} name ID-nazev vrstvy
	 * @param {Object} options options
	 * @return {SceneLayerCanvasGeom}
	 */
	createLayerCanvasGeom(id, options) {
		return this._postCreateLayer(new SceneLayerCanvasGeom(this, this._itemId++, id, options));
	}

	/**
	 * Vytvoreni vrstvy pro bile stopy.
	 *
	 * @param {String} name ID-nazev vrstvy
	 * @return {SceneLayerWinter}
	 */
	createLayerWinter(id, options) {
		return this._postCreateLayer(new SceneLayerWinter(this, this._itemId++, id, options));
	}

	/**
	 * Vytvoreni panoramy.
	 *
	 * @param  {Element} cont Kam panoramu pripnout?
	 * @return {ScenePano}
	 */
	createPanoScene(cont) {
		let pano = new ScenePano(this, this._itemId++, cont);

		// state
		this._state.pano.push(pano);

		// propagation
		this.featureChange(this, this.featureEvent("createPanoScene", true), [pano]);

		return pano;
	}

	getPanoDataUrl(pano) {
		return this._active.getPanoDataUrl(pano);
	}

	// geometrie

	/**
	 * Vytvoreni geometrie.
	 *
	 * @param  {Object} type Typ geometrie - viz. zacatek souboru
	 * @param  {String|Number} [id] Volitelne ID
	 * @param  {SceneCoords} coords Souradnice geometrie
	 * @param  {Object} options
	 * @return {SceneGeometry}
	 */
	createGeometry(type, id, coords, options) {
		return this._postCreateGeometry(new SceneGeometry(this, this._itemId++, type, id, coords, options));
	}

	/**
	 * Vytvoreni multi geometrie ze vstupnich dat.
	 *
	 * @param  {Object} data Vstupni data
	 * @return {SceneGeometryMulti}
	 */
	createGeometryMulti(data, options) {
		return this._postCreateGeometry(new SceneGeometryMulti(this, this._itemId++, data, options));
	}

	/**
	 * Vytvoreni smart geometrie ze vstupnich dat.
	 *
	 * @param  {Object} data Vstupni data
	 * @return {SceneGeometryMulti}
	 */
	createGeometrySmart(type, id, coords?, options?) {
		return new SceneGeometrySmart(this, this._itemId++, type, id, coords, options);
	}

	/**
	 * Vytvoreni multi smart geometrie ze vstupnich dat.
	 *
	 * @param  {Object} data Vstupni data
	 * @return {SceneGeometryMulti}
	 */
	createGeometrySmartMulti(data, options?) {
		return new SceneGeometrySmartMulti(this, this._itemId++, data, options);
	}

	/**
	 * Vytvoreni smart geometrie pro planovani mhd ze vstupnich dat.
	 *
	 * @param  {Object} data Vstupni data
	 * @return {SceneGeometryMulti}
	 */
	createGeometrySmartPubt(type, id, coords?, options?) {
		return new SceneGeometrySmartPubt(this, this._itemId++, type, id, coords, options);
	}

	/**
	 * Vytvoreni dopravni multi geometrie ze vstupnich dat.
	 *
	 * @param  {Object} data Vstupni data
	 * @return {SceneGeometryTraffic}
	 */
	createGeometryTraffic(data) {
		return this._postCreateGeometry(new SceneGeometryTraffic(this, this._itemId++, data));
	}

	// ovladaci prvky

	/**
	 * Vytvoreni easter eggu
	 *
	 * @return {SceneControlEgg}
	 */
	createControlEgg(opts) {
		return this._postCreateControl(new SceneControlEgg(this, this._itemId++, opts));
	}

	/**
	 * Vytvoreni kontextoveho menu.
	 *
	 * @return {SceneControlContextMenu}
	 */
	createControlContextMenu() {
		return this._postCreateControl(new SceneControlContextMenu(this, this._itemId++));
	}

	setMapset3D(mapset) {
		if (this.isM3dActive()) {
			this._active.setMapset(mapset);
		}
	}

	set3DInteraction(bool) {
		this._active.setInteraction(bool);
	}

	setMapsetWasm(mapset: string) {
		if (this.isWasmActive()) {
			this._active.setMapset(mapset);
		}
	}

	updateCopyrights() {
		// aktivni vrstvy
		const activeLayers = this._state.layers.filter(layer => layer.isActive());
		const copyrightObjs: Array<ICopyright> = activeLayers.map(item => item.getCopyright()).filter(item => item && Object.keys(item).length > 0);
		const { zoom } = store.getState().map;

		setCopyrights(zoom, copyrightObjs);
	}

	/* *********************** private *************************************** */

	/**
	 * Mapovani SMap -> Scena
	 */
	_mappingId() {
		// u mapsetu to neni konstanta, bo potrebujeme stejne hodnoty
		const layersDef = getLayersDef();

		this._layersDef = layersDef;
		this._mapset.BASE = layersDef.DEF_BASE;
		this._mapset.OPHOTO = layersDef.DEF_OPHOTO;
		this._mapset.PANO = layersDef.DEF_PANO;
		this._mapset.TURIST = layersDef.DEF_TURIST;
		this._mapset.OPHOTO0203 = layersDef.DEF_OPHOTO0203;
		this._mapset.OPHOTO0406 = layersDef.DEF_OPHOTO0406;
		this._mapset.OPHOTO1012 = layersDef.DEF_OPHOTO1012;
		this._mapset.OPHOTO1415 = layersDef.DEF_OPHOTO1415;
		this._mapset.OPHOTO1618 = layersDef.DEF_OPHOTO1618;
		this._mapset.HYBRID = layersDef.DEF_HYBRID;
		this._mapset.TRAIL = layersDef.DEF_TRAIL;
		this._mapset.BIKE = layersDef.DEF_BIKE;
		this._mapset.HISTORIC = layersDef.DEF_HISTORIC;
		this._mapset.TURIST_WINTER = layersDef.DEF_TURIST_WINTER;
		this._mapset.SUMMER = layersDef.DEF_SUMMER;
		this._mapset.GEOGRAPHY = layersDef.DEF_GEOGRAPHY;
		this._mapset.OBLIQUE = layersDef.DEF_OBLIQUE;
		this._mapset.OBLIQUE_HYBRID = layersDef.DEF_OBLIQUE_HYBRID;

		// vizualni veci
		// marker poi
		this._visual.MARKER_POI = SMap.Marker.POI.VISUAL;
		this._visual.MARKER_WINDY = SMap.WMMarker.TYPES;
	}

	_setMapserConfig() {
		// config
		this._config = getLayersConfig({
			mapServer: CONFIG.mapserver.url,
		});

		// nastavime i do smap
		const allLayersDef = Object.keys(this._layersDef);

		for (const layerDef of allLayersDef) {
			const layerId = this._layersDef[layerDef];

			SMap.CONFIG[layerId] = this._config[layerId];
		}

		// uprava crossOrigin u dlazdic - jak mapserver, tak tilecache
		SMap.TileSet.img = node("img", {
			alt: "",
			crossOrigin: CONFIG.mapserver.crossOrigin,
		});
		SMap.TileSet.img.style.position = "absolute";
	}

	/**
	 * Seznam vsech signalu, ktere podporuje scena a cast z nich proxuje na providera.
	 */
	_registerSignals() {
		// muze volat nekdo zvenku
		this._signals["map-redraw"] = {};
		this._signals["marker-click"] = {};
		this._signals["zoom-start"] = {};
		this._signals["zoom-step"] = {};
		this._signals["zoom-stop"] = {};
		this._signals["marker-drag-start"] = {};
		this._signals["marker-drag-stop"] = { data: ["coords"] };
		this._signals["marker-drag-move"] = { data: ["coords"] };
		this._signals["pano-availCheck"] = { data: ["avail"] };
		this._signals["marker-poi-enter"] = {};
		this._signals["marker-poi-leave"] = {};
		this._signals["marker-enter"] = {};
		this._signals["marker-leave"] = {};
		this._signals["markers-ready"] = {};
		this._signals["map-pan"] = {};

		// interni
		this._signals["marker-poi-click"] = { noProvider: true };
		this._signals["geometry-click"] = { noProvider: true };
		this._signals["geometry-mouseover"] = { noProvider: true };
		this._signals["geometry-mouseout"] = { noProvider: true };
		this._signals["geometry-canvas-mouseover"] = { noProvider: true };
		this._signals["geometry-canvas-mouseout"] = { noProvider: true };
		this._signals["geometry-multi-click"] = { noProvider: true };
		this._signals["geometry-canvas-click"] = { noProvider: true };
		this._signals["layer-enable"] = { noProvider: true };
		this._signals["layer-disable"] = { noProvider: true };
		this._signals["marker-change"] = { noProvider: true };

		// interni + smap
		this._signals["pano-change"] = { noProvider: true, data: ["camera", "place"] };
		this._signals["pano-place"] = { noProvider: true, data: ["place"] };
		this._signals["pano-timeline"] = { noProvider: true, data: ["year"] };
		this._signals["provider-change"] = { noProvider: true };
		this._signals["provider-changed"] = { noProvider: true };

		// make
		this._signals["geoloc-open"] = { noProvider: true };
		this._signals["geoloc-close"] = { noProvider: true };
		this._signals["marker-hover-action"] = { noProvider: true };
	}

	/**
	 * Bind signalu - pokud jsou signaly interni pro scenu (noProvider), tak se nic nedeje, jinak se
	 * signal propaguje do providera. Nyni to funguje tak, ze scena si drzi stav nazvu signalu, takze neco
	 * takoveho: { "map-redraw": [signal1, signal2], "zoom-start": [signal3] }
	 *
	 * @param {Object} signalItem Data signalu vytvorene v addListener
	 * @private
	 */
	_addSignal(signalItem) {
		if (signalItem.noProvider) return;

		let addSignal = false;

		if (!(signalItem.name in this._state.listeners)) {
			this._state.listeners[signalItem.name] = [];
			addSignal = true;
		}

		// state
		this._state.listeners[signalItem.name].push(signalItem);

		if (addSignal) {
			// propagation
			this.featureChange(this, this.featureEvent("addListener"), [signalItem.name]);
		}
	}

	/**
	 * Odebrani signalu - pokud je signal vytvoreny nad providerem, tak nejdrive odebereme signal ve scene, pokud
	 * se k danemu signalu (jeho nazvu) nevazou zadne bindovane signaly ze sceny, odebereme ho i nad providerem.
	 *
	 * @param {Object} signalItem Data signalu vytvorene v addListener
	 * @private
	 */
	_removeSignal(signalItem) {
		if (signalItem.noProvider) return;

		if (signalItem.name in this._state.listeners) {
			let nameSignals = this._state.listeners[signalItem.name];
			let ind = nameSignals.indexOf(signalItem);

			if (ind != -1) {
				// state
				nameSignals.splice(ind, 1);
			}

			if (!nameSignals.length) {
				this.featureChange(this, this.featureEvent("removeListener"), [signalItem.name]);
				delete this._state.listeners[signalItem.name];
			}
		}
	}

	/**
	 * Pomocna metoda pro vytvoreni markeru - upravi stav sceny a posle signal na providera.
	 */
	_postCreateMarker<T extends SceneMarker>(sceneMarker: T): T {
		// state
		this._state.markers[sceneMarker.itemId] = sceneMarker;

		// propagation
		this.featureChange(this, this.featureEvent("createMarker", true), arguments);

		return sceneMarker;
	}

	/**
	 * Pomocna metoda pro vytvoreni vrstvy - upravi stav sceny a posle signal na providera.
	 */
	_postCreateLayer<T extends SceneLayer>(sceneLayer: T): T {
		// state
		this._state.layers.push(sceneLayer);

		// propagation
		this.featureChange(this, this.featureEvent("createLayer", true), arguments);

		return sceneLayer;
	}

	/**
	 * Pomocna metoda pro vytvoreni geometrie - upravi stav sceny a posle signal na providera.
	 *
	 * @param  {SceneGeometry} sceneGeometry Vstupni marker
	 * @return {SceneGeometry}
	 */
	_postCreateGeometry(sceneGeometry) {
		// state
		this._state.geometries[sceneGeometry.itemId] = sceneGeometry;

		// propagation
		this.featureChange(this, this.featureEvent("createGeometry", true), arguments);

		return sceneGeometry;
	}

	/**
	 * Pomocna metoda pro vytvoreni ovladaciho prvku - upravi stav sceny a posle signal na providera.
	 *
	 * @param  {SceneControl} sceneControl Vstupni marker
	 * @return {SceneControl}
	 */
	_postCreateControl(sceneControl) {
		// state
		this._state.controls.push(sceneControl);

		// propagation
		this.featureChange(this, this.featureEvent("createControl", true), [sceneControl]);

		return sceneControl;
	}

	_updateTileUrl(item) {
		const lang = window.Loader.lang;

		if (item.langs) {
			let tileLang = "";

			if (lang in item.langs && lang != "cs" && lang != "sk") {
				tileLang = lang;
			} else if (lang != "cs" && lang != "sk" && "en" in item.langs) {
				tileLang = "en";
			}

			if (tileLang) {
				item.url = item.langs[tileLang].url || item.url;
				item.retinaUrl = item.langs[tileLang].retinaUrl || item.retinaUrl;
			}
		}
	}
}

export default new Scene();
